/* Campo data in italiano: `gg/mm/aaaa` verso l'occhio, ISO `aaaa-mm-gg` verso il codice.
 *
 * PERCHÉ ESISTE. `<input type="date">` non sceglie il formato dal documento: lo
 * sceglie dalla LINGUA DEL BROWSER, che dalla pagina non è raggiungibile. Con
 * Chrome in inglese il 5 settembre 2026 compare come `09/05/2026`, e un italiano
 * lo legge «9 maggio». Su una schermata dove si sceglie il periodo di un
 * backtest questo non è un fastidio estetico: un periodo letto al contrario
 * misura mesi diversi da quelli voluti, e il risultato è sbagliato senza
 * sembrarlo.
 *
 * COME. Il campo che si vede e si scrive è un `<input type="text">` che parla
 * italiano. Il `<input type="date">` resta, ma solo come SELETTORE: è
 * trasparente, sovrapposto al tasto del calendario, fuori dal giro dei tab. Non
 * si riscrive un calendario a mano perché su telefono il selettore di sistema è
 * molto meglio di qualunque imitazione — ed è l'unica parte in cui il formato
 * del browser non fa danno, perché lì si tocca un giorno su una griglia, non si
 * legge una data scritta in cifre.
 *
 * IL CONTRATTO VERSO CHI CHIAMA NON CAMBIA: `value` entra in ISO, `onChange`
 * esce con `e.target.value` in ISO e `e.target.name` come passato. Le date
 * mezze scritte, impossibili o fuori intervallo escono come stringa VUOTA, mai
 * come una data diversa da quella scritta: il campo mostra l'errore e il modulo
 * non riceve un periodo inventato.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
} from 'react';

/* ---------- conversioni e lettura ---------- */

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Una o due cifre per giorno e mese: chi scrive `5/9/2026` non sta sbagliando. */
const ITALIANA = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
/** ISO incollata e già passata per la normalizzazione delle barre. */
const ROVESCIA = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/;

const MESI = [
  'gennaio',
  'febbraio',
  'marzo',
  'aprile',
  'maggio',
  'giugno',
  'luglio',
  'agosto',
  'settembre',
  'ottobre',
  'novembre',
  'dicembre',
];

function due(n: number): string {
  return String(n).padStart(2, '0');
}

/** Quanti giorni ha il mese: l'anno serve, perché febbraio cambia. */
export function giorniDelMese(mese: number, anno: number): number {
  return new Date(Date.UTC(anno, mese, 0)).getUTCDate();
}

/** ISO `2026-09-05` → `05/09/2026`. Vuoto se non è una data ISO. */
export function italianaDaIso(iso: string | null | undefined): string {
  const m = String(iso ?? '').slice(0, 10).match(ISO);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

export type EsitoData =
  | { stato: 'vuoto'; iso: '' }
  | { stato: 'parziale'; iso: '' }
  | { stato: 'impossibile'; iso: ''; messaggio: string }
  | { stato: 'valida'; iso: string };

/**
 * Legge quello che c'è scritto. Non corregge: una data impossibile torna
 * impossibile con scritto perché, invece di scivolare sul giorno dopo — è quello
 * che fa `new Date(2026, 1, 31)`, che risponde 3 marzo senza dire niente.
 */
export function leggiData(testo: string): EsitoData {
  const t = testo.trim();
  if (t === '') return { stato: 'vuoto', iso: '' };

  let giorno: number;
  let mese: number;
  let anno: number;

  const ita = ITALIANA.exec(t);
  const rov = ita ? null : ROVESCIA.exec(t);
  if (ita) {
    giorno = Number(ita[1]);
    mese = Number(ita[2]);
    anno = Number(ita[3]);
  } else if (rov) {
    anno = Number(rov[1]);
    mese = Number(rov[2]);
    giorno = Number(rov[3]);
  } else {
    return { stato: 'parziale', iso: '' };
  }

  if (anno < 1900 || anno > 2999) {
    return {
      stato: 'impossibile',
      iso: '',
      messaggio: `L'anno ${anno} è fuori scala: scrivilo per intero, es. 2026.`,
    };
  }
  if (mese < 1 || mese > 12) {
    return {
      stato: 'impossibile',
      iso: '',
      messaggio: `Il mese ${due(mese)} non esiste: i mesi vanno da 01 a 12.`,
    };
  }
  if (giorno < 1) {
    return { stato: 'impossibile', iso: '', messaggio: 'Il giorno 00 non esiste: si parte da 01.' };
  }
  const quanti = giorniDelMese(mese, anno);
  if (giorno > quanti) {
    return {
      stato: 'impossibile',
      iso: '',
      messaggio: `Il ${giorno} ${MESI[mese - 1]} ${anno} non esiste: quel mese ha ${quanti} giorni.`,
    };
  }
  return { stato: 'valida', iso: `${anno}-${due(mese)}-${due(giorno)}` };
}

/** Le ISO si confrontano come stringhe: l'ordine alfabetico è quello del tempo. */
function fuoriIntervallo(iso: string, min?: string, max?: string): string | null {
  if (min && ISO.test(min) && iso < min) {
    return `Data troppo indietro: la prima disponibile è ${italianaDaIso(min)}.`;
  }
  if (max && ISO.test(max) && iso > max) {
    return `Data troppo avanti: l'ultima disponibile è ${italianaDaIso(max)}.`;
  }
  return null;
}

/* ---------- impaginazione di quello che si sta scrivendo ---------- */

const CAPIENZA = [2, 2, 4];

/**
 * Mette il testo nella forma `gg/mm/aaaa` senza combattere chi scrive.
 *
 * Le cifre in eccesso di un gruppo scendono in quello dopo, così `05/091`
 * diventa `05/09/1` invece di perdere l'ultima cifra. Le barre automatiche si
 * aggiungono SOLO quando si sta scrivendo in coda (`appende`): a metà parola, o
 * mentre si cancella, la struttura resta quella che c'è, altrimenti il cursore
 * salta e la barra appena cancellata ricompare da sola.
 */
function impagina(t: string, appende: boolean): string {
  const parti = t.split('/');
  const gruppi = ['', '', ''];
  let riporto = '';
  for (let i = 0; i < 3; i++) {
    const pezzo = riporto + (parti[i] ?? '');
    gruppi[i] = pezzo.slice(0, CAPIENZA[i]);
    riporto = pezzo.slice(CAPIENZA[i]);
  }

  let barre = Math.min(2, parti.length - 1);
  if (gruppi[1] !== '' && barre < 1) barre = 1;
  if (gruppi[2] !== '' && barre < 2) barre = 2;
  if (appende) {
    if (gruppi[0].length === CAPIENZA[0] && barre < 1) barre = 1;
    if (barre >= 1 && gruppi[1].length === CAPIENZA[1] && barre < 2) barre = 2;
  }

  let s = gruppi[0];
  if (barre >= 1) s += '/' + gruppi[1];
  if (barre >= 2) s += '/' + gruppi[2];
  return s;
}

/** Quello che arriva dalla tastiera o dagli appunti, ridotto alla forma del campo. */
export function normalizza(grezzo: string, appende: boolean): string {
  /* Una ISO incollata per intero si riconosce prima di toccarla: `2026-09-05`
     ha i trattini dove l'italiano ha le barre, e va letta al rovescio. */
  const iso = ISO.exec(grezzo.trim());
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;

  let t = grezzo
    .replace(/[.\-\s]+/g, '/')
    .replace(/[^\d/]/g, '')
    .replace(/\/{2,}/g, '/');
  if (t.startsWith('/')) t = t.slice(1);
  return impagina(t, appende);
}

function contaCifre(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) if (s[i] >= '0' && s[i] <= '9') n++;
  return n;
}

function dopoLaCifra(s: string, n: number): number {
  if (n <= 0) return 0;
  let visti = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] >= '0' && s[i] <= '9') {
      visti++;
      if (visti === n) return i + 1;
    }
  }
  return s.length;
}

/* ---------- icona ---------- */

function IconaCalendario({ dim = 15 }: { dim?: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

/* ---------- il campo ---------- */

export interface PropsCampoData
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  className?: string;
  /** Serve solo a nominare il tasto del calendario per chi legge lo schermo. */
  etichetta: string;
  /** L'errore lo scopre questo campo, ma lo disegna `Campo`: sotto, come gli altri. */
  onErrore?: (messaggio: string | null) => void;
}

export function CampoData({
  className = '',
  etichetta,
  onErrore,
  value,
  onChange,
  onBlur,
  min,
  max,
  disabled,
  required,
  name,
  id,
  placeholder = 'gg/mm/aaaa',
  ...resto
}: PropsCampoData) {
  const isoEsterno = String(value ?? '').slice(0, 10);
  const isoMin = min == null ? undefined : String(min);
  const isoMax = max == null ? undefined : String(max);

  const [testo, setTesto] = useState(() => italianaDaIso(isoEsterno));
  /* "Data incompleta" non va detto alla prima cifra: chi scrive lo sa già. */
  const [toccato, setToccato] = useState(false);

  const rifTesto = useRef<HTMLInputElement>(null);
  const rifNativo = useRef<HTMLInputElement>(null);
  const rifCursore = useRef<number | null>(null);
  /* L'ultimo ISO uscito di qui. Serve a distinguere «il padre ha ricevuto quello
     che gli ho mandato» da «il padre ha cambiato data per conto suo» — solo nel
     secondo caso il testo scritto va sovrascritto. */
  const rifEmesso = useRef(isoEsterno);

  const esito = leggiData(testo);
  const oltre = esito.stato === 'valida' ? fuoriIntervallo(esito.iso, isoMin, isoMax) : null;
  const messaggio =
    esito.stato === 'impossibile'
      ? esito.messaggio
      : oltre
        ? oltre
        : esito.stato === 'parziale' && toccato
          ? 'Data incompleta: scrivila come gg/mm/aaaa.'
          : null;

  /* Il valore arriva da fuori (una finestra preimpostata, un caricamento): il
     testo si riallinea. Se invece è l'eco di quello che ho appena mandato, no —
     riscriverlo mentre si scrive fa saltare il cursore. */
  useEffect(() => {
    if (isoEsterno !== rifEmesso.current) {
      rifEmesso.current = isoEsterno;
      setTesto(italianaDaIso(isoEsterno));
    }
  }, [isoEsterno]);

  useEffect(() => {
    onErrore?.(messaggio);
  }, [messaggio, onErrore]);

  /* Smontandosi porta via il proprio errore: un messaggio rosso che sopravvive
     al campo che lo ha prodotto è peggio di nessun messaggio. Le dipendenze
     restano vuote di proposito — serve solo la pulizia finale. */
  const rifErrore = useRef(onErrore);
  rifErrore.current = onErrore;
  useEffect(
    () => () => {
      rifErrore.current?.(null);
    },
    [],
  );

  /* Anche la validazione nativa deve saperlo: `required` da solo vedrebbe il
     testo pieno e lascerebbe passare `31/02/2026`, che come data non c'è. */
  useEffect(() => {
    rifTesto.current?.setCustomValidity(messaggio ?? '');
  }, [messaggio]);

  useLayoutEffect(() => {
    const p = rifCursore.current;
    if (p == null) return;
    rifCursore.current = null;
    const el = rifTesto.current;
    if (el && document.activeElement === el) el.setSelectionRange(p, p);
  });

  /** Verso l'esterno esce sempre e solo ISO, col nome che il chiamante ha dato. */
  function emetti(iso: string, origine?: Event) {
    rifEmesso.current = iso;
    if (!onChange) return;
    const bersaglio = {
      value: iso,
      name: name ?? '',
      id: id ?? '',
      type: 'date',
    } as unknown as HTMLInputElement;
    const nulla = () => {};
    const falso = () => false;
    onChange({
      target: bersaglio,
      currentTarget: bersaglio,
      type: 'change',
      bubbles: true,
      cancelable: false,
      defaultPrevented: false,
      eventPhase: 2,
      isTrusted: false,
      timeStamp: Date.now(),
      nativeEvent: origine ?? new Event('change'),
      preventDefault: nulla,
      stopPropagation: nulla,
      isDefaultPrevented: falso,
      isPropagationStopped: falso,
      persist: nulla,
    } as unknown as ChangeEvent<HTMLInputElement>);
  }

  function scrive(e: ChangeEvent<HTMLInputElement>) {
    const el = e.currentTarget;
    const grezzo = el.value;
    const sel = el.selectionStart ?? grezzo.length;
    const tipo = (e.nativeEvent as InputEvent).inputType ?? '';
    const appende = sel >= grezzo.length && !tipo.startsWith('delete');

    const nuovo = normalizza(grezzo, appende);
    rifCursore.current = appende ? nuovo.length : dopoLaCifra(nuovo, contaCifre(grezzo.slice(0, sel)));
    setTesto(nuovo);
    if (nuovo !== '') setToccato(false);

    const es = leggiData(nuovo);
    const buona = es.stato === 'valida' && !fuoriIntervallo(es.iso, isoMin, isoMax);
    const iso = buona ? es.iso : '';
    if (iso !== rifEmesso.current) emetti(iso, e.nativeEvent);
  }

  function esce(e: FocusEvent<HTMLInputElement>) {
    setToccato(true);
    /* `5/9/2026` diventa `05/09/2026`: stesso giorno, scritto per bene. Nessuna
       data viene cambiata qui, solo riscritta nella forma piena. */
    const es = leggiData(testo);
    if (es.stato === 'valida') {
      const canonico = italianaDaIso(es.iso);
      if (canonico !== testo) setTesto(canonico);
    }
    onBlur?.(e);
  }

  function dalCalendario(e: ChangeEvent<HTMLInputElement>) {
    const iso = e.target.value;
    setTesto(italianaDaIso(iso));
    setToccato(false);
    if (iso !== rifEmesso.current) emetti(iso, e.nativeEvent);
  }

  /** Apre il selettore di sistema. Da tastiera passa di qui; col dito e col
   *  mouse il clic finisce direttamente sull'`input[type=date]` trasparente. */
  function apriCalendario() {
    const el = rifNativo.current;
    if (!el || disabled) return;
    try {
      const conPicker = el as HTMLInputElement & { showPicker?: () => void };
      if (typeof conPicker.showPicker === 'function') {
        conPicker.showPicker();
        return;
      }
    } catch {
      /* Alcuni browser rifiutano `showPicker` se non riconoscono il gesto:
         si ripiega sul vecchio modo. */
    }
    el.focus();
    el.click();
  }

  return (
    <>
      {/* `...resto` per PRIMO: `Campo` ci passa anche il `type="date"` del
          chiamante, e qui il tipo deve restare `text`. Tutto quello che viene
          dopo vince. */}
      <input
        {...resto}
        ref={rifTesto}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        maxLength={10}
        className={className}
        placeholder={placeholder}
        value={testo}
        onChange={scrive}
        onBlur={esce}
        disabled={disabled}
        required={required}
      />
      <span className="sb-campo__calendario">
        <button
          type="button"
          className="sb-campo__calendario-tasto"
          aria-label={`Apri il calendario: ${etichetta}`}
          disabled={disabled}
          onClick={apriCalendario}
        >
          <IconaCalendario />
        </button>
        {/* Non ha `name` e non è raggiungibile col tab: esiste solo per aprire il
            selettore di sistema. Il valore del modulo lo dice `onChange`.
            Col dito e col mouse il clic arriva qui — il CSS allarga il suo
            «tasto calendario» interno fino a coprire tutto il riquadro — e la
            tendina di sistema si apre da sola, senza una riga di JavaScript. */}
        <input
          ref={rifNativo}
          type="date"
          className="sb-campo__calendario-nativo"
          tabIndex={-1}
          aria-hidden="true"
          value={esito.stato === 'valida' && !oltre ? esito.iso : ''}
          min={isoMin}
          max={isoMax}
          disabled={disabled}
          onChange={dalCalendario}
        />
      </span>
    </>
  );
}
